import type { FeedbackType } from "@asurion-core/expert-workspace-feedback";

import { FeedbackModal } from "@asurion-core/expert-workspace-feedback";
import { useExpertUniversityConfiguration } from "@expert/expert-university";
import { Button, Text } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { type JSX, useMemo } from "react";

import { useAnalytics } from "@/analytics";
import { env as configEnv } from "@/config";
import { useFeedbackNotification } from "@/feedback";
import { isSimulatedVoiceTask } from "@/sdk";
import { getSelectedSessionId, isChatTask, isVoiceTask, useActiveTask, usePartner } from "@/sdk";
import { useLogger } from "@/sdk/logging/LoggerProvider";
import { useFeatureFlag, userCache } from "@/shared-utils";

import { env as customEnv } from "../Profile/environment";
import { MegaphoneIcon } from "./MegaphoneIcon";

export function FeedbackButton({
    feedbackType,
    variant = "button",
    isTraining,
    sessionID,
}: {
    feedbackType: FeedbackType;
    variant?: "button" | "transparent";
    isTraining?: boolean;
    sessionID?: string;
}): JSX.Element {
    const logger = useLogger({ module: "WorkspaceFeedbackButton", supportTeam: "solve" });
    const { dispatcher } = useAnalytics();

    const triggerFeedbackNotification = useFeedbackNotification();

    const task = useActiveTask();
    const isExpertUniversityVoiceTask = isSimulatedVoiceTask(task);

    let partner = usePartner();

    const { trainingPartner } = useExpertUniversityConfiguration();
    if (isTraining && trainingPartner !== partner) {
        partner = trainingPartner;
    }
    const { data: isInLiveKitPilot } = useFeatureFlag(
        "EWP-isInLiveKitPilot",
        { profile: isExpertUniversityVoiceTask ? task.profileId : undefined, partner: trainingPartner },
        { isEnabled: isExpertUniversityVoiceTask },
    );
    const expertUniversityAppName = isInLiveKitPilot ? `expert-university-pilot` : "expert-university";
    const sessionId = sessionID ?? getSelectedSessionId();
    const ssoAccessToken = userCache.ssoAccessToken ?? "";

    const loggerWithContext = useMemo(
        () =>
            logger.child({
                sessionId,
                channel: configEnv.channel,
                ...(isVoiceTask(task) && { callSid: task?.agentCallId }),
            }),
        [logger, sessionId, task],
    );

    const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure(false, {
        onOpen: () => {
            void dispatcher.dispatchAction("Click", "ShareFeedback", { source: "Header" });
            loggerWithContext.info("Feedback modal in Workspace Header opened");
        },
        onClose: () => {
            triggerFeedbackNotification("success");
            loggerWithContext.info("Feedback modal in Menu closed");
        },
    });

    const error = () => triggerFeedbackNotification("error");

    const formattedVersionNumber = customEnv.versionNumber.startsWith("v")
        ? `v${customEnv.versionNumber.substring(3)}`
        : customEnv.versionNumber;

    const appName = isTraining ? expertUniversityAppName : "expert-workspace";
    /**
     * Extra data to be sent along with the feedback
     * only chat workspaces care about the chat id and task id
     * all workspaces care about the workspace version
     */
    const extraData = {
        ...(isChatTask(task) && {
            "Chat ID": task?.chatId,
            "Task ID": task?.id,
            "Messaging Topic": task?.topic,
            "Source App": task?.sourceApp,
            "Neglection Count": task?.neglectionCount,
        }),
        "Workspace Version": formattedVersionNumber,
    };

    return (
        <>
            <FeedbackModal
                isOpen={modalOpened}
                onClose={closeModal}
                onError={error}
                feedbackPayload={{
                    appName,
                    caller: "PlatformHeader",
                    callSid: isVoiceTask(task) ? task?.agentCallId : undefined, //TODO: support chat better
                    channel: configEnv.channel,
                    feedbackType,
                    partner,
                    sessionId,
                    ssoAccessToken,
                    extraData,
                    isTraining: isTraining,
                }}
                logger={loggerWithContext}
            />
            {variant === "transparent" ? (
                <Text
                    id="training-report-error"
                    size="sm"
                    mr="auto"
                    style={{ cursor: "pointer", textDecoration: "underline", color: "var(--mantine-color-gray-5)" }}
                    onClick={openModal}
                >
                    Report error
                </Text>
            ) : (
                <Button
                    color="indigo.7"
                    id="header-feedback-button"
                    leftSection={<MegaphoneIcon />}
                    onClick={openModal}
                    size="xs"
                    fz="xs"
                    variant="filled"
                >
                    Feedback
                </Button>
            )}
        </>
    );
}
